import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { captureError } from "@/lib/sentry";
import { LightningIcon, BookmarkSimpleIcon, CheckIcon, ChatCircleDotsIcon, CrosshairIcon, GitForkIcon } from "@phosphor-icons/react";

// ---- field mapping (catcher's view) ----
const W = 400, H = 300;
const sx = (px) => 200 + px * 64;
const sy = (pz) => 283 - pz * 40;
const PLATE_HALF = 0.83;

const BREAK = {
  FF: [0, -7], FA: [0, -7], SI: [9, 6], FT: [9, 6],
  SL: [-15, 4], ST: [-22, 4], SV: [-15, 4],
  CU: [7, 16], KC: [7, 16], CS: [7, 16],
  CH: [7, 10], FS: [5, 12], FO: [5, 12], FC: [-9, 2],
};

function fmtName(full) {
  if (!full) return "—";
  const parts = full.trim().split(" ");
  const first = parts[0];
  const last = parts.slice(1).join(" ") || first;
  return `${last}, ${first[0]}`;
}

function initials(full) {
  if (!full) return "–";
  const p = full.trim().split(" ");
  return ((p[0]?.[0] || "") + (p[p.length - 1]?.[0] || "")).toUpperCase();
}

function locationLabel(px, pz, top, bot, side) {
  if (px == null || pz == null) return "—";
  const vert = pz > top ? "High" : pz < bot ? "Low" : "Middle";
  const outside = side === "R" ? px > 0.3 : px < -0.3;
  const inside = side === "R" ? px < -0.3 : px > 0.3;
  const horiz = outside ? "Outside" : inside ? "Inside" : "Middle";
  if (vert === "Middle" && horiz === "Middle") return "Down the Middle";
  return `${vert} ${horiz}`.replace("Middle ", "").replace(" Middle", "").trim();
}

function Avatar({ name, color }) {
  return (
    <div className="w-10 h-10 rounded-full flex items-center justify-center font-display font-bold text-sm shrink-0"
      style={{ backgroundColor: `${color}26`, color, border: `1.5px solid ${color}80` }}>
      {initials(name)}
    </div>
  );
}

function BaseDiamond({ runners }) {
  const on = runners || {};
  const c = (f) => (f ? "#FDB927" : "rgba(255,255,255,0.14)");
  return (
    <svg viewBox="0 0 44 30" width="42" height="28" data-testid="base-diamond">
      <rect x="17" y="3" width="9" height="9" transform="rotate(45 21.5 7.5)" fill={c(on.second)} stroke="#000" strokeWidth="0.5" />
      <rect x="6" y="11" width="9" height="9" transform="rotate(45 10.5 15.5)" fill={c(on.third)} stroke="#000" strokeWidth="0.5" />
      <rect x="28" y="11" width="9" height="9" transform="rotate(45 32.5 15.5)" fill={c(on.first)} stroke="#000" strokeWidth="0.5" />
    </svg>
  );
}

function Outs({ n = 0 }) {
  return (
    <div className="flex items-center gap-1 mt-0.5" data-testid="outs-dots">
      {[0, 1, 2].map((i) => (
        <span key={i} className={`w-1.5 h-1.5 rounded-full ${i < (n || 0) ? "bg-[#FF453A]" : "bg-white/20"}`} />
      ))}
    </div>
  );
}

function Batter({ side }) {
  // spec: R hitter stands on LEFT (our left), L hitter on RIGHT
  const left = side !== "L";
  const tx = left ? 84 : 316;
  const flip = left ? 1 : -1;
  return (
    <g transform={`translate(${tx},0)`} opacity="0.92" style={{ pointerEvents: "none" }} data-testid="pitch-batter">
      <g transform={`scale(${flip},1)`}>
        {/* bat */}
        <rect x="4" y="120" width="46" height="5" rx="2.5" transform="rotate(-42 4 122)" fill="#b08440" />
        {/* arms up */}
        <path d="M-2 158 Q14 130 26 124" stroke="#cbd5e1" strokeWidth="9" fill="none" strokeLinecap="round" />
        {/* helmet + head */}
        <circle cx="-2" cy="150" r="11" fill="#1f2937" />
        <path d="M-14 150 a12 12 0 0 1 24 0 l3 0 a3 3 0 0 1 0 6 l-27 0 a3 3 0 0 1 0 -6 z" fill="#111827" />
        {/* torso */}
        <path d="M-12 162 Q-4 158 6 162 L10 210 Q-2 216 -16 210 Z" fill="#cbd5e1" />
        {/* belt */}
        <rect x="-16" y="206" width="28" height="5" fill="#1f2937" />
        {/* legs (stance) */}
        <path d="M-16 210 L-22 268 -12 270 -6 214 Z" fill="#9aa3af" />
        <path d="M8 210 L18 264 28 262 12 212 Z" fill="#9aa3af" />
        {/* shoes */}
        <rect x="-26" y="266" width="18" height="7" rx="3" fill="#0b1220" />
        <rect x="14" y="258" width="18" height="7" rx="3" transform="rotate(8 23 261)" fill="#0b1220" />
      </g>
    </g>
  );
}

function Stadium() {
  return (
    <g>
      <defs>
        <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0b1726" /><stop offset="100%" stopColor="#13243a" />
        </linearGradient>
        <linearGradient id="grass" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1f6b39" /><stop offset="100%" stopColor="#15512b" />
        </linearGradient>
        <radialGradient id="dirt" cx="50%" cy="100%" r="80%">
          <stop offset="0%" stopColor="#86512c" /><stop offset="100%" stopColor="#5d3a20" />
        </radialGradient>
      </defs>
      {/* sky / upper deck */}
      <rect x="0" y="0" width={W} height="118" fill="url(#sky)" />
      {/* stands */}
      <rect x="0" y="44" width={W} height="66" fill="#16202e" />
      {[...Array(40)].map((_, i) => (
        <rect key={i} x={(i * 10) % W} y={50 + ((i * 7) % 50)} width="6" height="3" fill="#26344a" opacity="0.6" />
      ))}
      {/* outfield wall */}
      <rect x="0" y="104" width={W} height="34" fill="#0e2233" />
      <rect x="22" y="110" width="78" height="20" rx="2" fill="#0A84FF" opacity="0.25" />
      <text x="61" y="124" textAnchor="middle" fontSize="11" fontWeight="800" fill="#9cc4ff" opacity="0.7">GAMLY</text>
      <rect x="160" y="110" width="80" height="20" rx="2" fill="#ffffff" opacity="0.08" />
      <text x="200" y="124" textAnchor="middle" fontSize="10" fontWeight="800" fill="#cbd5e1" opacity="0.55">GAMLY AI</text>
      <rect x="300" y="110" width="78" height="20" rx="2" fill="#FDB927" opacity="0.18" />
      <text x="339" y="124" textAnchor="middle" fontSize="9" fontWeight="800" fill="#fde08a" opacity="0.7">WATCH LIVE</text>
      {/* grass with mow stripes */}
      <rect x="0" y="135" width={W} height="165" fill="url(#grass)" />
      {[...Array(8)].map((_, i) => (
        <rect key={i} x={i * 50} y="135" width="25" height="165" fill="#ffffff" opacity="0.03" />
      ))}
      {/* infield dirt */}
      <ellipse cx={W / 2} cy={H} rx="250" ry="95" fill="url(#dirt)" />
      {/* foul lines */}
      <line x1="200" y1="280" x2="20" y2="210" stroke="#ffffff" strokeOpacity="0.4" strokeWidth="2" />
      <line x1="200" y1="280" x2="380" y2="210" stroke="#ffffff" strokeOpacity="0.4" strokeWidth="2" />
      {/* batter's boxes */}
      <rect x="120" y="236" width="34" height="56" rx="3" fill="#ffffff" opacity="0.05" />
      <rect x="246" y="236" width="34" height="56" rx="3" fill="#ffffff" opacity="0.05" />
      {/* home plate */}
      <polygon points="186,272 214,272 214,280 200,290 186,280" fill="#f8fafc" opacity="0.95" />
    </g>
  );
}

export default function PitchView({ gameId, gameStatus, game, language = "en", onAskGamly }) {
  const [data, setData] = useState(null);
  const [abIndex, setAbIndex] = useState(null);
  const [sel, setSel] = useState(null);
  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/games/${gameId}/pitches`, { params: abIndex != null ? { ab: abIndex } : {} });
      setData(r.data);
      if (r.data.available && r.data.at_bat?.pitches?.length) {
        const last = r.data.at_bat.pitches[r.data.at_bat.pitches.length - 1].number;
        setSel((prev) => (prev == null ? last : prev));
      }
    } catch (err) {
      captureError("pitches load failed:", err);
    }
  }, [gameId, abIndex]);

  useEffect(() => { setSel(null); }, [abIndex]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (gameStatus !== "LIVE" || abIndex != null) return undefined;
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [gameStatus, abIndex, load]);

  const ab = data?.at_bat;
  const state = data?.game_state || {};
  const selectedPitch = ab?.pitches?.find((p) => p.number === sel) || null;

  useEffect(() => {
    if (!ab || sel == null) return undefined;
    let alive = true;
    setInsightLoading(true); setInsight(null); setSaved(false);
    api.post(`/games/${gameId}/pitch-insight`, { ab_index: ab.ab_index, pitch_number: sel, mode: "analysis", language })
      .then((r) => alive && setInsight({ mode: "analysis", text: r.data.text }))
      .catch(() => alive && setInsight({ mode: "analysis", text: "Couldn't generate analysis right now." }))
      .finally(() => alive && setInsightLoading(false));
    return () => { alive = false; };
  }, [sel, ab?.ab_index, gameId, language]); // eslint-disable-line react-hooks/exhaustive-deps

  const runMode = async (mode) => {
    if (!ab || sel == null) return;
    setInsightLoading(true);
    try {
      const r = await api.post(`/games/${gameId}/pitch-insight`, { ab_index: ab.ab_index, pitch_number: sel, mode, language });
      setInsight({ mode, text: r.data.text });
    } catch {
      setInsight({ mode, text: "Couldn't generate right now." });
    } finally {
      setInsightLoading(false);
    }
  };

  const saveHighlight = async () => {
    if (!selectedPitch) return;
    try {
      await api.post("/highlights", {
        game_id: gameId, kind: "pitch",
        title: `${selectedPitch.type} · ${selectedPitch.mph ?? "?"} MPH`,
        detail: `${ab.batter} — ${selectedPitch.result}`,
        meta: { ab_index: ab.ab_index, pitch_number: selectedPitch.number },
      });
      setSaved(true);
    } catch (err) { captureError("save highlight failed:", err); }
  };

  if (data && !data.available) {
    return (
      <div className="p-6 rounded-md bg-[#121212] border border-white/10 text-center" data-testid="pitch-view-unavailable">
        <CrosshairIcon size={28} className="mx-auto mb-2 text-white/30" />
        <p className="text-sm text-white/60">{data.reason || "Pitch tracking is available for live MLB games."}</p>
      </div>
    );
  }
  if (!data || !ab) {
    return <div className="p-6 rounded-md bg-[#121212] border border-white/10 text-white/50 text-sm" data-testid="pitch-view-loading">Loading pitch data…</div>;
  }

  const plotted = ab.pitches.filter((p) => p.px != null && p.pz != null);
  const szTop = selectedPitch?.sz_top ?? plotted[0]?.sz_top ?? 3.18;
  const szBot = selectedPitch?.sz_bottom ?? plotted[0]?.sz_bottom ?? 1.6;
  const zL = sx(-PLATE_HALF), zR = sx(PLATE_HALF), zT = sy(szTop), zB = sy(szBot);
  const zW = zR - zL, zH = zB - zT;

  const battingHome = (ab.inning_label || "").startsWith("Bot");
  const batterColor = battingHome ? game.home_team_color : game.away_team_color;
  const pitcherColor = battingHome ? game.away_team_color : game.home_team_color;

  return (
    <div data-testid="pitch-view">
      {/* At-bat selector */}
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/45">{ab.inning_label} · {ab.result}</p>
        <select
          data-testid="pitch-ab-select"
          value={abIndex ?? "latest"}
          onChange={(e) => setAbIndex(e.target.value === "latest" ? null : Number(e.target.value))}
          className="bg-black border border-white/15 text-white/80 text-xs font-bold rounded-md px-2.5 py-1.5 max-w-[210px]"
        >
          <option value="latest">Latest at-bat</option>
          {data.at_bats.map((a) => (
            <option key={a.ab_index} value={a.ab_index}>{`${a.inning_label} · ${a.batter}`}</option>
          ))}
        </select>
      </div>

      {/* Centerpiece: stadium + zone + bottom bar */}
      <div className="rounded-lg overflow-hidden border border-white/10" data-testid="pitch-field">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block" preserveAspectRatio="xMidYMid slice">
          <Stadium />
          <Batter side={ab.bat_side} />

          {/* strike zone 3x3 */}
          <rect x={zL} y={zT} width={zW} height={zH} fill="#3b82f6" fillOpacity="0.18" stroke="#0b0f14" strokeOpacity="0.85" strokeWidth="1.5" data-testid="strike-zone" />
          {[1, 2].map((i) => (
            <line key={`v${i}`} x1={zL + (zW / 3) * i} y1={zT} x2={zL + (zW / 3) * i} y2={zB} stroke="#0b0f14" strokeOpacity="0.7" strokeWidth="1" />
          ))}
          {[1, 2].map((i) => (
            <line key={`h${i}`} x1={zL} y1={zT + (zH / 3) * i} x2={zR} y2={zT + (zH / 3) * i} stroke="#0b0f14" strokeOpacity="0.7" strokeWidth="1" />
          ))}

          {/* trajectories */}
          {plotted.map((p) => {
            const ex = sx(p.px), ey = sy(p.pz);
            const rx = 200 + (ab.pitch_hand === "L" ? 20 : -20), ry = 78;
            const br = BREAK[p.type_code] || [0, 0];
            const cx = (rx + ex) / 2 + br[0], cy = (ry + ey) / 2 + br[1];
            const active = p.number === sel;
            return (
              <path key={`t${p.number}`} d={`M ${rx} ${ry} Q ${cx} ${cy} ${ex} ${ey}`} fill="none"
                stroke={p.color} strokeWidth={active ? 4 : 2.5} strokeOpacity={active ? 0.95 : 0.34} strokeLinecap="round"
                pathLength="1" className={active ? "gamly-pitch-anim" : ""}
                style={active ? { filter: `drop-shadow(0 0 5px ${p.color})` } : undefined} />
            );
          })}

          {/* pitch dots */}
          {plotted.map((p) => {
            const cx = sx(p.px), cy = sy(p.pz);
            const active = p.number === sel;
            return (
              <g key={`c${p.number}`} onClick={() => setSel(p.number)} style={{ cursor: "pointer" }} data-testid={`pitch-dot-${p.number}`}>
                <circle cx={cx} cy={cy} r={active ? 13 : 10} fill={p.color} fillOpacity={active ? 1 : 0.6} stroke="#fff" strokeWidth={active ? 2.5 : 1.5} />
                <text x={cx} y={cy + 3.5} textAnchor="middle" fontSize="11" fontWeight="800" fill="#0a0a0a">{p.number}</text>
              </g>
            );
          })}
        </svg>
        <style>{`
          .gamly-pitch-anim { stroke-dasharray: 1; stroke-dashoffset: 1; animation: gamlyPitchDraw 0.75s ease-out forwards; }
          @keyframes gamlyPitchDraw { to { stroke-dashoffset: 0; } }
        `}</style>

        {/* Bottom info bar */}
        <div className="grid grid-cols-3 gap-2 items-center bg-[#0d1117] px-3 sm:px-4 py-3" data-testid="pitch-bottom-bar">
          <div className="flex items-center gap-2.5 min-w-0">
            <Avatar name={ab.pitcher} color={pitcherColor} />
            <div className="min-w-0">
              <p className="font-display font-bold text-sm uppercase tracking-tight truncate" data-testid="bottom-pitcher">{fmtName(ab.pitcher)} <span className="text-white/40">{ab.pitch_hand}HP</span></p>
              <p className="text-[11px] text-white/55 truncate">{ab.pitcher_line || "—"}</p>
              {ab.pitcher_pitches ? <p className="text-[11px] text-white/40">{ab.pitcher_pitches} Pitches</p> : null}
            </div>
          </div>

          <div className="flex flex-col items-center" data-testid="pitch-count">
            <BaseDiamond runners={state.runners} />
            <p className="font-num text-sm font-black mt-1">{Math.min(state.balls ?? 0, 3)} - {Math.min(state.strikes ?? 0, 2)}</p>
            <Outs n={state.outs} />
          </div>

          <div className="flex items-center gap-2.5 flex-row-reverse min-w-0 text-right">
            <Avatar name={ab.batter} color={batterColor} />
            <div className="min-w-0">
              <p className="font-display font-bold text-sm uppercase tracking-tight truncate" data-testid="bottom-batter"><span className="text-white/40">{ab.bat_side}HB</span> {fmtName(ab.batter)}</p>
              <p className="text-[11px] text-white/55 truncate">{ab.batter_line || "—"}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pitch history */}
      <div className="mt-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 mb-2">Pitch History</p>
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="pitch-history">
          {ab.pitches.map((p) => (
            <button key={p.number} onClick={() => setSel(p.number)} data-testid={`pitch-history-${p.number}`}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold transition-colors ${p.number === sel ? "border-white/60 bg-white/10" : "border-white/10 hover:border-white/30"}`}>
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
              {p.type.split(" ").pop()} {p.mph ?? ""}
            </button>
          ))}
        </div>
      </div>

      {/* Pitch card + Gamly AI */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 mt-3">
        {selectedPitch && (
          <div className="lg:col-span-2 p-4 rounded-md bg-[#121212] border border-white/10" data-testid="pitch-card">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-black uppercase tracking-wider text-white/40">Pitch #{selectedPitch.number}</span>
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: selectedPitch.color }} />
            </div>
            <p className="font-display font-bold text-2xl uppercase tracking-tight mt-1" data-testid="pitch-type">{selectedPitch.type}</p>
            <p className="font-num text-3xl font-black" data-testid="pitch-mph">{selectedPitch.mph ?? "—"}<span className="text-sm text-white/40 ml-1">MPH</span></p>
            <p className="text-sm text-white/60 mt-1">{locationLabel(selectedPitch.px, selectedPitch.pz, szTop, szBot, ab.bat_side)}</p>
            <p className="text-sm mt-1"><span className="text-white/40">Result: </span><span className="font-semibold text-white/90">{selectedPitch.result}</span></p>
          </div>
        )}

        <div className="lg:col-span-3 p-4 rounded-md bg-[#121212] border border-[#0A84FF]/30" data-testid="pitch-ai">
          <div className="flex items-center gap-1.5 mb-2">
            <LightningIcon weight="fill" size={15} className="text-[#0A84FF]" />
            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-[#0A84FF]">Gamly AI</span>
          </div>
          {insightLoading ? (
            <p className="text-sm text-white/50" data-testid="pitch-ai-loading">Gamly is breaking down the pitch…</p>
          ) : (
            <p className="text-sm text-white/85 leading-relaxed" data-testid="pitch-ai-text">{insight?.text}</p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
            <button onClick={() => runMode("why")} disabled={insightLoading} data-testid="pitch-why" className="py-2 rounded-md border border-white/15 text-white/75 hover:border-white/30 text-[11px] font-bold uppercase tracking-wider">Why It Matters</button>
            <button onClick={() => onAskGamly?.()} data-testid="pitch-ask" className="py-2 rounded-md border border-white/15 text-white/75 hover:border-white/30 text-[11px] font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1"><ChatCircleDotsIcon size={13} /> Ask</button>
            <button onClick={() => runMode("predict")} disabled={insightLoading} data-testid="pitch-predict" className="py-2 rounded-md border border-[#FDB927]/40 text-[#FDB927] hover:bg-[#FDB927]/10 text-[11px] font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1"><GitForkIcon size={13} /> Predict</button>
            <button onClick={saveHighlight} data-testid="pitch-save" className={`py-2 rounded-md border text-[11px] font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1 ${saved ? "border-[#32D74B]/50 text-[#32D74B]" : "border-white/15 text-white/75 hover:border-white/30"}`}>
              {saved ? <><CheckIcon size={13} /> Saved</> : <><BookmarkSimpleIcon size={13} /> Save</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
